import { useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useApp } from '../AppContext'
import { useAsync } from '../hooks/useAsync'
import { hasCapability } from '../auth/roles'
import { PageHeader } from '../components/primitives/PageHeader'
import { Panel } from '../components/primitives/Panel'
import { DataTable, type DataTableColumn } from '../components/primitives/DataTable'
import { Mono } from '../components/primitives/Fact'
import { Field } from '../components/primitives/Field'
import { Notice } from '../components/primitives/Notice'
import { refusalSentence } from '../theme/vocabulary'
import { ApiError } from '../api/client'
import type { AccountListResponse, AccountSummary } from '../api/types'

// Customers list (ATC-130 / R-ATC-10).
//
// REPLACES the type-an-accountId lookup this page used to be. Under
// `ATC-ADR-013` an account was created BY BEING MENTIONED — neither
// Add-AccountEnvironment nor Grant-MigrationLicense checked existence — so a
// typo silently created a second, invisible account and mapped or granted into
// it. The old page asked an operator to type an identifier from memory, which
// was both the only available design and a direct generator of that failure.
//
// NO BALANCE ON THIS PAGE. A balance is an unbounded ledger partition scan that
// grows with lifetime migration volume; N of them per page, serially, on an
// HA-off Consumption app. The detail view fetches exactly one.
export function CustomersList() {
  const { api, role } = useApp()
  const canCreate = hasCapability(role, 'write')

  // Synced to the URL so a filtered list is shareable and, more importantly, so
  // the wizard's recovery link can point at one.
  const [params, setParams] = useSearchParams()
  const search = params.get('search') ?? ''

  const [searchInput, setSearchInput] = useState(search)
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [created, setCreated] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  const accounts = useAsync<AccountListResponse>(
    () => api.listAccounts(search ? { search } : {}),
    [search, nonce],
  )

  function submitSearch(e: FormEvent) {
    e.preventDefault()
    const next = new URLSearchParams(params)
    if (searchInput.trim()) {
      next.set('search', searchInput.trim())
    } else {
      next.delete('search')
    }
    setParams(next, { replace: true })
  }

  async function submitCreate(e: FormEvent) {
    e.preventDefault()
    setCreating(true)
    setCreateError(null)
    setCreated(null)
    try {
      const result = await api.createAccount(newName.trim())
      setCreated(result.accountId)
      setNewName('')
      setNonce((n) => n + 1)
    } catch (err) {
      setCreateError(
        err instanceof ApiError
          ? refusalSentence(err.code, err.message)
          : err instanceof Error
            ? err.message
            : String(err),
      )
    } finally {
      setCreating(false)
    }
  }

  const columns: DataTableColumn<AccountSummary>[] = [
    {
      key: 'name',
      header: 'Customer',
      render: (a) =>
        a.accountName ? (
          <Link to={`/customers/${a.accountId}`}>{a.accountName}</Link>
        ) : (
          // A grant against a previously-unseen account touches the registry
          // with a BLANK name, because that path has no accountName in scope.
          // The account is visible and correctable rather than invented.
          <Link to={`/customers/${a.accountId}`} className="muted">
            No name yet
          </Link>
        ),
    },
    {
      key: 'accountId',
      header: 'Account ID',
      render: (a) => <Mono>{a.accountId}</Mono>,
    },
    {
      key: 'environments',
      header: 'Environments',
      numeric: true,
      // `null` is NOT 0. The server distinguishes "no environments" from
      // "the membership scan failed", and collapsing them here would undo that.
      render: (a) =>
        a.environmentCount === null ? (
          <span className="muted">unknown</span>
        ) : (
          <Mono>{a.environmentCount}</Mono>
        ),
    },
    { key: 'createdAt', header: 'Created', render: (a) => <Mono>{a.createdAt}</Mono> },
  ]

  return (
    <section>
      <PageHeader
        title="Customers"
        actions={
          canCreate && (
            <Link to="/customers/onboard" className="btn-primary">
              Onboard customer
            </Link>
          )
        }
      />

      <form className="toolbar" onSubmit={submitSearch}>
        <input
          type="search"
          placeholder="Search by name…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <button type="submit">Search</button>
        {search && (
          <button
            type="button"
            onClick={() => {
              setSearchInput('')
              const next = new URLSearchParams(params)
              next.delete('search')
              setParams(next, { replace: true })
            }}
          >
            Clear
          </button>
        )}
      </form>

      {search && (
        <p className="muted">
          Name search matches the start of a name, within the current page only — full-text search
          across every page is a future search index.
        </p>
      )}

      {canCreate && (
        <Panel title="New customer">
          <form className="inline-form" onSubmit={submitCreate}>
            <div className="row">
              <Field label="Customer name" required>
                <input
                  type="text"
                  placeholder="Acme MSP"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  maxLength={128}
                />
              </Field>
              <button type="submit" className="btn-primary" disabled={creating || !newName.trim()}>
                {creating ? 'Creating…' : 'Create customer'}
              </button>
            </div>
            <p className="muted">
              The account ID is generated by the server. There is deliberately no field for it —
              typing an identifier is the mistake this page removes.
            </p>
            {createError && <Notice tone="error">{createError}</Notice>}
            {created && (
              <Notice tone="ok">
                Created <Mono>{created}</Mono>.
              </Notice>
            )}
          </form>
        </Panel>
      )}

      {accounts.error && <Notice tone="error">{accounts.error}</Notice>}

      <Panel title="Customers" flush>
        <DataTable
          caption="Migration-licensing customers"
          columns={columns}
          data={accounts.data?.accounts ?? []}
          keyForRow={(a) => a.accountId}
          isLoading={accounts.loading}
          error={accounts.error ? 'Could not load customers.' : undefined}
          emptyState={
            search ? 'No customers match that name on this page.' : 'No customers yet.'
          }
        />
      </Panel>

      {accounts.data?.continuationToken && (
        <p className="muted">
          More customers exist beyond this page.
        </p>
      )}
    </section>
  )
}
